import { useTheme } from '@rneui/themed'
import { useRoutingContext } from 'app/routing_context'
import { useSendMessageContext } from 'chat/send_message_context'
import { ComposeScreenProps } from 'home/stack/types'
import { t } from 'app/i18l'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { showHeaderButton } from 'shared/header/header_button'

import { AttachmentFile } from '@nextforce/api'
import { useCompaniesContext } from 'company/context'
import { useCompanyPreferences } from 'company/queries/list'
import { confirmationAlert } from 'shared/alerts'
import { MAX_MESSAGE_LENGTH } from 'shared/constants'
import { useRecentStoresContext } from 'stores/context'
import { useManagersList } from './queries/fetch_managers'
import { useComposeMessageRecipients } from './queries/list'
import { ComposeGroup, ComposeUser, Recipient } from './queries/shared/types'
import { COMPOSE_SCREEN_ID, ComposeView } from './view'

export function ComposeScreen({ navigation, route }: ComposeScreenProps) {
  const { storeId, message } =
    route.params?.type === 'messageManagers'
      ? route.params
      : { storeId: undefined, message: undefined }

  const { conversationId, name } =
    route.params?.type === 'messageGroup'
      ? route.params
      : { conversationId: undefined, name: undefined }

  const { activeStoreId } = useRecentStoresContext()
  const { handleNavigation } = useRoutingContext()
  const { theme } = useTheme()
  const [searchTerm, setSearchTerm] = useState<string>('')
  const isMounted = useRef(true)
  const [selectedRecipients, setSelectedRecipients] = useState<
    (ComposeGroup | ComposeUser)[]
  >(
    conversationId
      ? [
          {
            id: conversationId,
            title: name,
            type: 'group',
          },
        ]
      : []
  )
  const [isUrgentMessage, setIsUrgentMessage] = useState<boolean>(false)
  const {
    getSelectedAttachments,
    getMessageBody,
    setMessageBody,
    onSendComposeMessage,
    pasteAttachments,
  } = useSendMessageContext()
  const messageBody = useMemo(
    () => getMessageBody(COMPOSE_SCREEN_ID),
    [getMessageBody]
  )
  const selectedAttachments = useMemo(
    () => getSelectedAttachments(COMPOSE_SCREEN_ID),
    [getSelectedAttachments]
  )
  const [loading, setLoading] = useState(false)
  const { activeCompany } = useCompaniesContext()
  const { data: companyPreferences } = useCompanyPreferences(activeCompany?.id)

  const recipientsQueryWithoutStoreIdQuery = useComposeMessageRecipients(
    !storeId
  )
  const isReadOnly = !!storeId
  const recipientsWithStoreIdQuery = useManagersList(storeId)

  useEffect(() => {
    if (recipientsWithStoreIdQuery.data) {
      setSelectedRecipients(recipientsWithStoreIdQuery.data)
    }
  }, [recipientsWithStoreIdQuery.data])

  const onRecipientPress = useCallback(
    (recipient: Recipient) => {
      setSelectedRecipients([...selectedRecipients, recipient])
    },
    [selectedRecipients]
  )

  const setComposeMessageBody = useCallback(
    (body: string) => {
      setMessageBody(COMPOSE_SCREEN_ID, body)
    },
    [setMessageBody]
  )

  const onSelectedRecipientPress = useCallback(
    (recipient: Recipient) => {
      setSelectedRecipients([
        ...selectedRecipients.filter(m => m.id !== recipient.id),
      ])
    },
    [selectedRecipients]
  )

  const onHandleBackspace = () => {
    setSelectedRecipients(
      selectedRecipients.slice(0, selectedRecipients.length - 1)
    )
  }

  const pasteMessageAttachments = useCallback(
    (files: AttachmentFile[]) => {
      pasteAttachments(COMPOSE_SCREEN_ID, files)
    },
    [pasteAttachments]
  )

  const onSendMessage = useCallback(async () => {
    const groupIds = selectedRecipients
      .filter(m => m.type === 'group')
      .flatMap(m => Number(m.id))
    const userIds = selectedRecipients
      .filter(m => m.type === 'user')
      .flatMap(m => Number(m.id))

    if (isUrgentMessage) {
      const confirmed = await confirmationAlert(
        t('urgentMessage'),
        t('urgentMessageConfirmationDescription')
      )
      if (!confirmed) {
        return
      }
    }
    try {
      setLoading(true)
      const composeMessageEvent = await onSendComposeMessage({
        chatGroupId: COMPOSE_SCREEN_ID,
        groupIds,
        userIds,
        storeId: storeId ?? activeStoreId,
        isReadOnly,
        isUrgent: isUrgentMessage,
      })
      setLoading(false)
      if (!isMounted.current) {
        return
      }
      navigation.pop()
      if (composeMessageEvent?.addMessageEvents) {
        if (
          composeMessageEvent?.conversation &&
          groupIds.length === 0 &&
          !isReadOnly
        ) {
          handleNavigation({
            routeName: 'messages',
            routeParams: {
              id: composeMessageEvent?.conversation.id,
            },
          })
        } else if (userIds.length === 0 && groupIds.length === 1) {
          handleNavigation({
            routeName: 'messages',
            routeParams: {
              id: groupIds[0].toString(),
            },
          })
        }
      }
    } catch (e) {
      setLoading(false)
    }
  }, [
    selectedRecipients,
    onSendComposeMessage,
    storeId,
    activeStoreId,
    isReadOnly,
    isUrgentMessage,
    navigation,
    handleNavigation,
  ])

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  useEffect(() => {
    showHeaderButton({
      navigation,
      title: t('send'),
      disabled:
        selectedRecipients.length === 0 ||
        (messageBody.trim().length === 0 && selectedAttachments.length === 0) ||
        messageBody.length >= MAX_MESSAGE_LENGTH,
      onPress: onSendMessage,
      position: 'right',
      useIcon: false,
      theme: theme,
      loading,
    })
  }, [
    loading,
    messageBody,
    messageBody.length,
    navigation,
    onSendMessage,
    selectedAttachments.length,
    selectedRecipients.length,
    theme,
  ])

  useEffect(() => {
    if (message && messageBody.length === 0) {
      setMessageBody(COMPOSE_SCREEN_ID, message)
    }
  }, [message, messageBody.length, setMessageBody])

  const filteredRecipients = useMemo(
    () =>
      getFilteredRecipients(
        recipientsQueryWithoutStoreIdQuery.data?.filter(
          m => selectedRecipients.findIndex(i => i.id === m.id) === -1
        ),
        searchTerm
      ),
    [recipientsQueryWithoutStoreIdQuery.data, searchTerm, selectedRecipients]
  )

  return (
    <ComposeView
      recipients={filteredRecipients}
      selectedRecipients={selectedRecipients}
      onRecipientPress={onRecipientPress}
      onSelectedRecipientPress={onSelectedRecipientPress}
      setSearchTerm={setSearchTerm}
      setMessageBody={setComposeMessageBody}
      pasteMessageAttachments={pasteMessageAttachments}
      messageBody={messageBody}
      onHandleBackspace={onHandleBackspace}
      isLoading={recipientsQueryWithoutStoreIdQuery.isFetching}
      isReadOnly={isReadOnly}
      enableUrgentMessage={companyPreferences?.enableUrgentMessage}
      isUrgentMessage={isUrgentMessage}
      setIsUrgentMessage={setIsUrgentMessage}
    />
  )
}

function getFilteredRecipients(
  recipients: (ComposeGroup | ComposeUser)[] | undefined,
  searchTerm: string
) {
  const filteredRecipients = recipients?.filter(recipient => {
    switch (recipient.type) {
      case 'group':
        return recipient.title?.toLowerCase().includes(searchTerm.toLowerCase())
      case 'user':
        const nameTags = recipient.firstName + ' ' + recipient.lastName
        const roleTags = recipient.roles?.join(' ')
        const jobTitleTags = recipient.jobTitle
        const storeNumberTags = recipient.storeId

        const allTags = [
          nameTags,
          roleTags,
          jobTitleTags,
          storeNumberTags,
        ].join(' ')
        return allTags.toLowerCase().includes(searchTerm.toLowerCase())
    }
  })
  if (filteredRecipients) {
    return filteredRecipients.slice(0, 100)
  }
}
